import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchRevenueData, fetchFinanceData } from '../../../store/actions/accountingDashboardActions'
import { fetchPayments } from '../../../store/actions/paymentsActions'
import Loader from '../../../components/elements/Loader'
import DataTable from '../../../components/elements/DataTable'
import { tableHeadersFields, transactionTimeStamp } from '../../../utils'
import OrderStatus from '../../../components/partials/OrderStatus'
import { Link } from 'react-router-dom'
import ArrowIcon from '../../../components/elements/icons/ArrowIcon'
import EmptyState from '../../../components/elements/EmptyState'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
)

const AccountingDashboard = () => {
  const dispatch = useDispatch()
  const accountingDashboardSelector = useSelector((state) => state.accountingDashboard)
  const paymentsSelector = useSelector((state) => state.payments)
  
  const [selectedPeriod, setSelectedPeriod] = useState('monthly')
  // const [chartPeriod, setChartPeriod] = useState('monthly')

  useEffect(() => {
    dispatch(fetchRevenueData(selectedPeriod))
    dispatch(fetchFinanceData())
    dispatch(fetchPayments('', 1, 10))
  }, [dispatch, selectedPeriod])

  // Extract finance data
  const financeData = accountingDashboardSelector.financeData || {}
  const overview = financeData.overview || {}
  const invoices = financeData.invoices || {}
  const sundryAccounts = financeData.sundryAccounts || {}
  
  // Extract revenue data
  const revenueData = Array.isArray(accountingDashboardSelector.revenueData) 
    ? accountingDashboardSelector.revenueData 
    : []
  
  // Calculate totals
  const totalRevenue = revenueData.reduce((sum, item) => sum + (item.totalRevenue || 0), 0)
  const totalTransactions = revenueData.reduce((sum, item) => sum + (item.transactionCount || 0), 0)
  const averageTransactionValue = totalTransactions > 0 ? totalRevenue / totalTransactions : 0
  
  // Get current period data (most recent)
  const currentPeriodData = revenueData[0] || {}
  const currentPeriodRevenue = currentPeriodData.totalRevenue || 0
  // const currentPeriodTransactions = currentPeriodData.transactionCount || 0

  // Format period label
  const formatPeriodLabel = (item) => {
    if (item._id?.year && item._id?.month) {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${monthNames[item._id.month - 1]} ${item._id.year}`
    }
    return 'N/A'
  }

  // Generate chart data from API revenue data
  const generateChartData = () => {
    // Take only the first 10 records (most recent)
    const recentData = revenueData.slice(0, 10)
    
    // If we have data, pad to 10 records
    let chartData = [...recentData]
    
    if (chartData.length < 10 && chartData.length > 0) {
      // Get the oldest date from existing data
      const oldestItem = chartData[chartData.length - 1]
      const oldestYear = oldestItem._id?.year
      const oldestMonth = oldestItem._id?.month
      
      if (oldestYear && oldestMonth) {
        // Generate missing periods going backwards
        let currentYear = oldestYear
        let currentMonth = oldestMonth
        
        while (chartData.length < 10) {
          // Go back one month
          currentMonth--
          if (currentMonth < 1) {
            currentMonth = 12
            currentYear--
          }
          
          // Add placeholder data
          chartData.push({
            _id: { year: currentYear, month: currentMonth },
            totalRevenue: 0,
            transactionCount: 0
          })
        }
      }
    }
    
    // If no data at all, create 10 empty periods for current year
    if (chartData.length === 0) {
      const currentDate = new Date()
      let year = currentDate.getFullYear()
      let month = currentDate.getMonth() + 1
      
      for (let i = 0; i < 10; i++) {
        chartData.push({
          _id: { year, month },
          totalRevenue: 0,
          transactionCount: 0
        })
        
        month--
        if (month < 1) {
          month = 12
          year--
        }
      }
    }
    
    // Reverse to show oldest to newest (left to right)
    const sortedData = [...chartData].reverse()
    
    const labels = sortedData.map(item => {
      if (item._id?.year && item._id?.month) {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        return `${monthNames[item._id.month - 1]} ${item._id.year}`
      }
      return 'N/A'
    })
    
    const data = sortedData.map(item => (item.totalRevenue || 0) / 100)

    return {
      labels,
      datasets: [
        {
          label: 'Revenue',
          data,
          backgroundColor: 'rgba(138, 43, 226, 0.8)',
          borderColor: 'rgb(138, 43, 226)',
          borderWidth: 1,
        },
      ],
    }
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `Revenue: ₦${context.parsed.y.toLocaleString()}`
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return '₦' + (value / 1000000).toFixed(1) + 'M'
          }
        }
      }
    }
  }

  // Clean up payments data for table
  const TransactionLink = ({reference, index}) => (
    <button onClick={()=>console.log('Transaction clicked', index)} className='text-vcm-purple hover:text-gray-600 transition duration-200'>
      {reference}
    </button>
  )

  const TransactionAmount = ({amount}) => (
    <p className='font-medium font-space-grotesk'>₦ {amount ? (amount/100)?.toLocaleString() : 0}</p>
  )

  const cleanUpPaymentsData = (array) => {
    if(!array) return []
    return array.map((item, itemIndex) => ({
      reference: <TransactionLink reference={item.transaction?.transactionReference || 'N/A'} index={itemIndex} />,
      paymentFor: <p className='text-sm'>{item.fee ? `${item.fee?.name}` : item.invoiceAssignment?.invoiceName || 'N/A'}</p>,
      status: <OrderStatus status={item.transaction?.status || 'pending'} />,
      paidBy: <div><p className='text-sm'>{item.createdBy?.name || ''}</p></div>,
      amount: <TransactionAmount amount={item?.fee?.amount || item.invoiceAssignment?.amountPaid || 0} />,
      timeStamp: transactionTimeStamp(item.createdAt).date,
    }))
  }

  const paymentsColumnWidths = {
    reference: 'w-2/12',
    paymentFor: 'w-3/12',
    status: 'w-2/12',
    paidBy: 'w-2/12',
    amount: 'w-2/12',
    timeStamp: 'w-2/12',
  }

  const tableOptions = {
    selectable: false,
    clickableRows: false,
  }

  return (
    <div className='w-full'>
        {/* Header */}
        {/* <div className='flex items-center justify-between mb-6'>
          <h2 className='text-2xl font-bold text-gray-800'>Finance Dashboard</h2>
        </div> */}

        {/* Main Overview Cards */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mt-8 mb-8'>
          {/* Total Revenue Card */}
          <div className='bg-vcm-purple rounded-lg p-6 shadow-lg'>
            <p className='text-xs uppercase tracking-[0.2em] text-white text-opacity-80 mb-2'>Total Revenue</p>
            <h2 className='text-3xl font-bold text-white font-space-grotesk'>₦{((overview.totalRevenue || 0)/100)?.toLocaleString()}</h2>
            <p className='text-xs text-white text-opacity-70 mt-2'>{overview.totalTransactions || 0} transactions</p>
            <div className='mt-3 pt-3 border-t border-white border-opacity-20 space-y-1'>
              <div className='flex items-center justify-between text-xs'>
                <span className='text-white text-opacity-70'>Current Period:</span>
                <span className='text-white font-semibold font-space-grotesk'>₦{(currentPeriodRevenue/100)?.toLocaleString()}</span>
              </div>
              <div className='flex items-center justify-between text-xs'>
                <span className='text-white text-opacity-70'>{formatPeriodLabel(currentPeriodData)}</span>
                <span className='text-white text-opacity-70'>Avg: ₦{(averageTransactionValue/100)?.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Outstanding Balance */}
          <div className='bg-white rounded-lg p-6 shadow-sm border border-gray-200'>
            <p className='text-xs uppercase tracking-wider text-gray-500 mb-2'>Outstanding</p>
            <h2 className='text-2xl font-bold text-orange-600 font-space-grotesk'>₦{((overview.outstandingBalance || 0)/100)?.toLocaleString()}</h2>
            <Link to="/admin/accounting/invoices" className='text-xs text-vcm-purple hover:text-gray-600 transition duration-200 flex items-center gap-x-1 mt-2'>
              View invoices <ArrowIcon className="w-3 h-3" />
            </Link>
          </div>

          {/* Total Invoices */}
          <div className='bg-white rounded-lg p-6 shadow-sm border border-gray-200'>
            <p className='text-xs uppercase tracking-wider text-gray-500 mb-2'>Total Invoices</p>
            <h2 className='text-3xl font-bold text-vcm-purple font-space-grotesk'>{overview.totalInvoices || 0}</h2>
            <p className='text-xs text-gray-600 mt-2'>{overview.totalInvoiceAssignments || 0} assignments</p>
          </div>

          {/* Sundry Balance */}
          <div className='bg-white rounded-lg p-6 shadow-sm border border-gray-200'>
            <p className='text-xs uppercase tracking-wider text-gray-500 mb-2'>Sundry Balance</p>
            <h2 className={`text-2xl font-bold font-space-grotesk ${(sundryAccounts.totalBalance || 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
              ₦{((sundryAccounts.totalBalance || 0)/100)?.toLocaleString()}
            </h2>
            <p className='text-xs text-gray-600 mt-2'>{sundryAccounts.count || 0} accounts</p>
          </div>

          {/* Pending Invoices */}
          <div className='bg-white rounded-lg p-6 shadow-sm border border-gray-200'>
            <p className='text-xs uppercase tracking-wider text-gray-500 mb-2'>Pending Invoices</p>
            <h2 className='text-3xl font-bold text-orange-600 font-space-grotesk'>{invoices.pending || 0}</h2>
            <div className='flex items-center gap-x-3 mt-2 text-xs'>
              <span className='text-green-600 font-space-grotesk'>{invoices.paidInFull || 0} paid</span>
              <span className='text-blue-600 font-space-grotesk'>{invoices.partPaid || 0} partial</span>
            </div>
          </div>
        </div>

        {/* Revenue Trends Section */}
        <div className='bg-white rounded-lg p-6 shadow-sm border border-gray-200 mb-8'>
          <div className='flex items-center justify-between mb-6'>
            <h3 className='text-lg font-medium text-gray-800'>Revenue Trends by Period</h3>
            <div className='flex items-center gap-x-3'>
              <label className='text-sm text-gray-600'>Period:</label>
              <select 
                value={selectedPeriod} 
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className='px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-vcm-purple'
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>

          {accountingDashboardSelector.loadingRevenue ? (
            <Loader />
          ) : revenueData.length > 0 ? (
            <div className='h-[350px]'>
              <Bar data={generateChartData()} options={chartOptions} />
            </div>
          ) : (
            <EmptyState emptyStateText="No revenue data available" />
          )}
        </div>

        {/* Two Column Layout */}
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8'>
          {/* Invoice Revenue Breakdown */}
          <div className='lg:col-span-2 bg-white rounded-lg p-6 shadow-sm border border-gray-200'>
            <h3 className='text-lg font-medium text-gray-800 mb-4'>Invoice Revenue by Status</h3>
            {invoices.revenueByStatus && invoices.revenueByStatus.length > 0 ? (
              <div className='space-y-4'>
                {invoices.revenueByStatus.map((status, index) => (
                  <div key={index} className='border-b border-gray-100 pb-4 last:border-0'>
                    <div className='flex items-center justify-between mb-2'>
                      <div>
                        <p className='font-medium text-gray-800'>{status._id.replace(/_/g, ' ')}</p>
                        <p className='text-xs text-gray-500'>{status.count} invoices</p>
                      </div>
                      <div className='text-right'>
                        <p className='text-lg font-semibold text-vcm-purple font-space-grotesk'>
                          ₦{((status.totalAmount || 0)/100)?.toLocaleString()}
                        </p>
                        <p className='text-xs text-gray-600 font-space-grotesk'>
                          Paid: ₦{((status.amountPaid || 0)/100)?.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div className='w-full bg-gray-200 rounded-full h-2'>
                      <div 
                        className={`h-2 rounded-full ${status._id === 'PAID_IN_FULL' ? 'bg-green-500' : 'bg-orange-500'}`}
                        style={{width: `${status.totalAmount > 0 ? ((status.amountPaid / status.totalAmount) * 100) : 0}%`}}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState emptyStateText="No invoice data available" />
            )}
          </div>

          {/* Sundry Account Details */}
          <div className='bg-white rounded-lg p-6 shadow-sm border border-gray-200'>
            <h3 className='text-lg font-medium text-gray-800 mb-4'>Sundry Accounts</h3>
            <div className='space-y-4'>
              <div>
                <p className='text-xs text-gray-600 mb-1'>Total Balance</p>
                <p className={`text-2xl font-bold font-space-grotesk ${(sundryAccounts.totalBalance || 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                  ₦{((sundryAccounts.totalBalance || 0)/100)?.toLocaleString()}
                </p>
              </div>
              <div className='border-t border-gray-200 pt-4'>
                <div className='flex items-center justify-between mb-2'>
                  <span className='text-sm text-gray-600'>Positive Balances</span>
                  <span className='text-sm font-semibold text-green-600 font-space-grotesk'>
                    ₦{((sundryAccounts.positiveBalances || 0)/100)?.toLocaleString()}
                  </span>
                </div>
                <div className='flex items-center justify-between mb-2'>
                  <span className='text-sm text-gray-600'>Negative Balances</span>
                  <span className='text-sm font-semibold text-red-600 font-space-grotesk'>
                    ₦{((sundryAccounts.negativeBalances || 0)/100)?.toLocaleString()}
                  </span>
                </div>
                <div className='flex items-center justify-between'>
                  <span className='text-sm text-gray-600'>Total Accounts</span>
                  <span className='text-sm font-semibold text-vcm-purple font-space-grotesk'>
                    {sundryAccounts.count || 0}
                  </span>
                </div>
              </div>
              <Link to="/admin/accounting/sundry-accounts" className='text-xs text-vcm-purple hover:text-gray-600 transition duration-200 flex items-center gap-x-2 pt-2'>
                View sundry accounts <ArrowIcon className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Revenue Trends Chart */}
        {/* <div className='bg-white rounded-lg p-6 shadow-sm border border-gray-200 mb-8'>
          <div className='flex items-center justify-between mb-6'>
            <h3 className='text-lg font-medium text-gray-800'>Revenue Trends</h3>
            <div className='flex items-center gap-x-3'>
              <label className='text-sm text-gray-600'>Period:</label>
              <select 
                value={selectedPeriod} 
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className='px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-vcm-purple'
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>
          <div className='h-[300px]'>
            <Bar data={generateChartData()} options={chartOptions} />
          </div>
        </div> */}

        {/* Recent Payments Section */}
        <div className='bg-white rounded-lg p-6 shadow-sm border border-gray-200'>
          <div className='flex items-center justify-between mb-4'>
            <h3 className='text-lg font-medium text-gray-800'>Recent Payments</h3>
            <Link to="/admin/accounting/payments" className='text-sm text-vcm-purple hover:text-gray-600 transition duration-200 flex items-center gap-x-2'>
              View all payments <ArrowIcon className="w-4 h-4" />
            </Link>
          </div>
          {paymentsSelector.loadingPayments ? (
            <Loader />
          ) : paymentsSelector.payments?.payments?.length > 0 ? (
            <DataTable
              tableHeaders={tableHeadersFields(cleanUpPaymentsData(paymentsSelector.payments.payments)[0]).headers}
              tableData={cleanUpPaymentsData(paymentsSelector.payments.payments)}
              columnWidths={paymentsColumnWidths}
              columnDataStyles={{}}
              allFields={tableHeadersFields(cleanUpPaymentsData(paymentsSelector.payments.payments)[0]).fields}
              onSelectItems={()=>{}}
              tableOptions={tableOptions}
              pagination={null}
            />
          ) : (
            <EmptyState emptyStateText="No payments yet" />
          )}
        </div>
      </div>
  )
}

export default AccountingDashboard

